import { useState, useEffect } from 'react';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { safeJson } from '../../../utils';
import type { ChangeRequest, StakeholderResponse, ImplementationPlan } from '../../../types';
import OneDrivePicker, { type OneDriveFile } from '../../../components/OneDrivePicker';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function PlanSection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [concernsOpen, setConcernsOpen] = useState(false);
  const [concerns, setConcerns] = useState<StakeholderResponse[]>([]);

  const existingPlan = safeJson<Partial<ImplementationPlan>>(record.implementationPlan, {});

  const [approach,      setApproach]      = useState(existingPlan.approach ?? '');
  const [milestones,    setMilestones]    = useState(existingPlan.milestones ?? '');
  const [training,      setTraining]      = useState(existingPlan.trainingRequirements ?? '');
  const [comms,         setComms]         = useState(existingPlan.communicationPlan ?? '');
  const [risks,         setRisks]         = useState(existingPlan.risks ?? '');
  const [resources,     setResources]     = useState(existingPlan.resourceCosts ?? '');
  const [goLiveDate,    setGoLiveDate]    = useState(existingPlan.goLiveDate ?? '');
  const [stabilisation, setStabilisation] = useState(existingPlan.stabilisationPeriod ?? '');
  const [errors,        setErrors]        = useState<Record<string, string>>({});
  const [planFile,      setPlanFile]      = useState<OneDriveFile | null>(null);

  useEffect(() => {
    api.get<StakeholderResponse[]>(`/changes/${encodeURIComponent(cid)}/responses?affected=true`)
      .then(setConcerns).catch(() => {});
  }, [cid]);

  function validate() {
    const e: Record<string, string> = {};
    if (!approach)             e.approach      = 'Required.';
    if (!milestones.trim())    e.milestones    = 'Required.';
    if (!training.trim())      e.training      = 'Required.';
    if (!comms.trim())         e.comms         = 'Required.';
    if (!risks.trim())         e.risks         = 'Required.';
    if (!resources.trim())     e.resources     = 'Required.';
    if (!goLiveDate)           e.goLiveDate    = 'Required.';
    if (!stabilisation.trim()) e.stabilisation = 'Required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const planPayload: ImplementationPlan = {
        approach: approach as ImplementationPlan['approach'],
        milestones, trainingRequirements: training, communicationPlan: comms,
        risks, resourceCosts: resources, goLiveDate, stabilisationPeriod: stabilisation,
        planVersion: existingPlan.planVersion ?? 'v1.0',
        uploadedFileUrl: planFile?.url ?? existingPlan.uploadedFileUrl ?? '',
        uploadedFileName: planFile?.name ?? existingPlan.uploadedFileName ?? '',
      };
      await api.patch(`/changes/${encodeURIComponent(cid)}/plan`, {
        implementationPlan: JSON.stringify(planPayload),
        confirmedGolive: goLiveDate,
        status: 'Plan Submitted',
      });
      showToast('Plan submitted. Sign-off requests sent.', 'success');
      await onRefresh();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="section-panel">
      {/* Concerns collapsible */}
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
        <button
          type="button"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9375rem', fontWeight: 600, color: 'var(--foreground)' }}
          onClick={() => setConcernsOpen(p => !p)}
          aria-expanded={concernsOpen}
        >
          <span><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" style={{ color: 'var(--warn)' }} /> Stakeholder Concerns &amp; Support Needs</span>
          <i className="fa-solid fa-chevron-down" style={{ transition: 'transform 0.2s', transform: concernsOpen ? 'rotate(180deg)' : 'none', color: 'var(--ink-dim)' }} aria-hidden="true" />
        </button>
        {concernsOpen && (
          <div style={{ padding: '0 24px 16px' }}>
            {concerns.filter(r => r.concerns || r.supportNeeds || r.effortEstimate).length === 0
              ? <p className="text-muted">No concerns raised by stakeholders.</p>
              : concerns.filter(r => r.concerns || r.supportNeeds || r.effortEstimate).map(r => (
                <div key={r.id} className="stakeholder-row" style={{ marginBottom: 8 }}>
                  <div className="stakeholder-row__avatar">{r.teamLeadName.charAt(0).toUpperCase()}</div>
                  <div className="stakeholder-row__info">
                    <div className="stakeholder-row__name">{r.teamLeadName} <span className="badge badge--muted">{r.department}</span></div>
                    {r.concerns      && <div className="stakeholder-row__meta"><strong>Concerns:</strong> {r.concerns}</div>}
                    {r.supportNeeds  && <div className="stakeholder-row__meta"><strong>Support Needs:</strong> {r.supportNeeds}</div>}
                    {r.effortEstimate && <div className="stakeholder-row__meta"><strong>Effort:</strong> {r.effortEstimate}</div>}
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>

      <div className="section-panel__header">
        <h2 className="section-panel__title"><i className="fa-solid fa-clipboard-list" aria-hidden="true" /> Step 4 — Submit Implementation Plan</h2>
      </div>
      <div className="section-panel__body">
        {existingPlan.draft && (
          <div className="alert alert--info" style={{ marginBottom: 16 }}>
            <div className="alert__body">Your saved draft has been loaded below. Review it, complete any blank fields, then submit to request sign-offs.</div>
          </div>
        )}
        <div className="form-grid">
          {/* Approach */}
          <div className="form-group form-group--full">
            <label className="form-label">Implementation Approach <span className="required">*</span></label>
            <div className="pill-group">
              {['Pilot','Phased','Full Rollout'].map(a => (
                <button key={a} type="button" className={`pill-option${approach === a ? ' is-selected' : ''}`} onClick={() => { setApproach(a); setErrors(p => ({ ...p, approach: '' })); }}>{a}</button>
              ))}
            </div>
            {errors.approach && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.approach}</span>}
          </div>

          {/* Text fields */}
          {([
            ['milestones', 'Key Activities and Milestones', milestones, setMilestones, 4],
            ['training',   'Training and Support Requirements', training, setTraining, 4],
            ['comms',      'Communication Plan', comms, setComms, 4],
            ['risks',      'Risks and Mitigations', risks, setRisks, 4],
            ['resources',  'Resource and Cost Requirements', resources, setResources, 4],
          ] as const).map(([key, label, val, setter, rows]) => (
            <div key={key} className="form-group form-group--full">
              <label className="form-label" htmlFor={`plan-${key}`}>{label} <span className="required">*</span></label>
              <textarea id={`plan-${key}`} className={`form-control${errors[key] ? ' is-error' : ''}`}
                rows={rows} value={val} onChange={e => (setter as React.Dispatch<React.SetStateAction<string>>)(e.target.value)} />
              {errors[key] && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors[key]}</span>}
            </div>
          ))}

          <div className="form-group">
            <label className="form-label" htmlFor="plan-golive">Confirmed Go-Live Date <span className="required">*</span></label>
            <input id="plan-golive" type="date" className={`form-control${errors.goLiveDate ? ' is-error' : ''}`}
              value={goLiveDate} onChange={e => setGoLiveDate(e.target.value)} />
            {errors.goLiveDate && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.goLiveDate}</span>}
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="plan-stabilisation">Stabilisation Period <span className="required">*</span></label>
            <input id="plan-stabilisation" type="text" className={`form-control${errors.stabilisation ? ' is-error' : ''}`}
              value={stabilisation} placeholder="e.g. 4 weeks" onChange={e => setStabilisation(e.target.value)} />
            {errors.stabilisation && <span className="form-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {errors.stabilisation}</span>}
          </div>
        </div>

        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Plan Document (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <OneDrivePicker onSelect={setPlanFile} disabled={submitting} label="Attach from OneDrive" />
              {(planFile || existingPlan.uploadedFileUrl) && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: 'var(--ink-dim)' }}>
                  <i className="fa-solid fa-file-lines" style={{ color: 'var(--primary)' }} aria-hidden="true" />
                  {planFile
                    ? planFile.name
                    : <a href={existingPlan.uploadedFileUrl} target="_blank" rel="noopener noreferrer">{existingPlan.uploadedFileName || 'View attached document'}</a>
                  }
                  {planFile && (
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-dim)', padding: 0, lineHeight: 1 }} onClick={() => setPlanFile(null)} aria-label="Remove file">
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  )}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className={`btn btn--primary btn--lg${submitting ? ' is-loading' : ''}`} onClick={submit} disabled={submitting}>
              <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Submit Plan &amp; Request Sign-Offs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
